import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getDeviceId } from "../utils/deviceId";

const API = process.env.REACT_APP_API_URL;

const ForgotPassword = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const canSubmit = useMemo(() => {
    const emailOk = email.trim().length > 0;
    return emailOk && !loading;
  }, [email, loading]);

  const onSubmit = async () => {
    if (!canSubmit) return;

    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const res = await fetch(`${API}/api/auth/forgot-password`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "y-device-id": getDeviceId(),
        },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });

      // Por seguridad, muchos sistemas responden "ok" aunque el email no exista.
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data.error || "Could not process the request.");
        return;
      }

      // Safe UX message (does not reveal if email exists)
      setSuccess(
        data.message ||
          "If the email is registered, we will send you instructions to reset your password."
      );
    } catch (e) {
      console.error(e);
      setError("Server error, please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") onSubmit();
  };

  return (
    <div className="w-screen h-screen bg-gray-200 flex justify-center items-center">
      <div className="w-[90%] sm:w-[400px] md:w-[450px] lg:w-[550px] xl:w-[580px] bg-white rounded-2xl shadow-lg p-20 relative">
        <h2 className="text-2xl font-semibold text-gray-900 text-center mb-2">
          Reset Password
        </h2>

        <p className="text-sm text-gray-600 text-center mb-8">
          Enter your email and we will send you instructions to reset it.
        </p>

        {error && (
          <div className="text-red-500 text-sm mb-4 text-center">{error}</div>
        )}

        {success && (
          <div className="text-green-600 text-sm mb-4 text-center">
            {success}
          </div>
        )}

        {/* Email */}
        <div className="relative w-full mt-4">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={onKeyDown}
            required
            placeholder="Email"
            className="peer block w-full border-b border-gray-300 text-gray-900 placeholder-transparent focus:outline-none py-2"
            autoComplete="email"
          />
          <label
            className="
              absolute left-0 text-gray-400 text-base
              top-2 transition-all duration-200
              peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400
              peer-focus:-top-4 peer-focus:text-sm peer-focus:text-green-600
              peer-valid:-top-4 peer-valid:text-sm peer-valid:text-green-600
              pointer-events-none
            "
          >
            Email
          </label>
        </div>

        {/* Submit */}
        <button
          onClick={onSubmit}
          className={`w-full bg-green-500 hover:bg-green-600 text-white font-medium py-3 rounded-lg mb-6 mt-8 ${
            !canSubmit ? "opacity-70 cursor-not-allowed" : ""
          }`}
          disabled={!canSubmit}
        >
          {loading ? "Sending..." : "Send instructions"}
        </button>

        {/* Back to login */}
        <div className="text-center text-sm text-gray-900">
          <span>Already remembered your password?</span>
          <button
            onClick={() => navigate("/")}
            className="text-green-500 font-semibold ml-1"
            type="button"
          >
            Sign in
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
